import * as React from 'react';
import cx from 'classnames';

import { BlogPostCard } from '../BlogPostCard/BlogPostCard';
import { EventCard } from '../EventCard/EventCard';
import { OpensourceProjectCard } from '../OpensourceProjectCard/OpensourceProjectCard';
import { ClientCard } from '../ClientCard/ClientCard';
import { QuoteCard } from '../QuoteCard/QuoteCard';
import { ServiceCard } from '../ServiceCard/ServiceCard';
import { EmailNewsletterForm } from '../EmailNewsletterForm/EmailNewsletterForm';

import * as cs from './FancyList.module.css';

interface Props {
  entries: any[];
  largeTilesCount?: number;
  withEntityType?: boolean;
  upcomingEvents?: boolean;
  withSubscribe?: boolean;
  trackEvent?: string;
}

export const FancyList = ({
  entries,
  largeTilesCount = 0,
  withEntityType = true,
  upcomingEvents,
  withSubscribe,
  trackEvent,
}: Props) => {
  const renderCard = (entry: any, index: number) => {
    const entryNode = entry.node || entry;
    const { entryType, slug, title } = entryNode;
    const size = index < largeTilesCount ? 'lg' : 'md';
    const randomColorIndex = index % 6;

    if (entryType === 'subscribe') {
      return <EmailNewsletterForm type="list" data-theme-reset />;
    }

    if (entryType === 'service') {
      const { description, withIcons } = entryNode;

      return (
        <ServiceCard
          size={size}
          index={index}
          title={title}
          description={description}
          slug={slug}
          withIcons={withIcons}
          trackEvent={trackEvent}
        />
      );
    }

    if (entryType === 'post') {
      const {
        createdAt,
        dateISO,
        theme,
        categories,
        skills,
        services,
        coverImage,
        coverImageSmall,
        coverVideo,
        coverVideoLoop,
        bgImage,
        bgImageSmall,
        bgColorOverflow,
        isBgFullSize,
        logo,
      } = entryNode;
      return (
        <BlogPostCard
          size={size}
          slug={slug}
          title={title}
          date={createdAt}
          dateISO={dateISO}
          theme={theme}
          categories={categories}
          skills={skills}
          services={services}
          coverImage={size === 'lg' ? coverImage : coverImageSmall}
          coverVideo={coverVideo}
          coverVideoLoop={coverVideoLoop}
          bgImage={size === 'lg' ? bgImage : bgImageSmall}
          bgColorOverflow={bgColorOverflow}
          isBgFullSize={isBgFullSize}
          randomColorIndex={randomColorIndex}
          withEntityType={withEntityType}
          trackEvent={trackEvent}
          logo={logo}
        />
      );
    }

    if (entryType === 'event') {
      const {
        theme,
        categories,
        coverImage,
        coverImageSmall,
        bgImage,
        bgImageSmall,
        bgColorOverflow,
        services,
        skills,
        event: {
          dateStart,
          dateStartISO,
          title: eventTitle,
          city,
          dateStartMonth,
          dateStartDay,
          dateEndMonth,
          dateEndDay,
          upcoming,
        },
        type,
        authors,
        slides,
        talk,
        logo,
      } = entryNode;

      return (
        <EventCard
          size={
            index !== 0 && upcoming && size === 'lg' ? 'lg_condenced' : size
          }
          slug={slug}
          title={title}
          theme={theme}
          categories={categories}
          services={services}
          skills={skills}
          coverImage={size === 'lg' ? coverImage : coverImageSmall}
          bgImage={size === 'lg' ? bgImage : bgImageSmall}
          bgColorOverflow={bgColorOverflow}
          randomColorIndex={randomColorIndex}
          withEntityType={withEntityType}
          event={eventTitle}
          authors={authors}
          type={type}
          slides={slides?.slides_url}
          audio={talk?.audio_urls}
          video={talk?.video_url}
          live={talk?.live_url}
          city={city}
          upcomingEvents={upcomingEvents || upcoming}
          dateStart={dateStart}
          dateStartISO={dateStartISO}
          dateStartMonth={dateStartMonth}
          dateStartDay={dateStartDay}
          dateEndMonth={dateEndMonth}
          dateEndDay={dateEndDay}
          trackEvent={trackEvent}
          logo={logo}
        />
      );
    }

    if (entryType === 'client') {
      const {
        description,
        logo,
        services,
        skills,
        facts,
        industries,
        childClients,
        defaultTheme,
      } = entryNode;

      return (
        <ClientCard
          size={size}
          title={title}
          description={description}
          slug={slug}
          facts={facts}
          logo={logo}
          industries={industries}
          services={services}
          skills={skills}
          childClients={childClients}
          withEntityType={withEntityType}
          theme={defaultTheme}
          trackEvent={trackEvent}
        />
      );
    }

    if (entryNode.entryType === 'quote') {
      const { name, about, text, logo } = entryNode;
      return (
        <QuoteCard
          name={name}
          about={about}
          text={text}
          slug={slug}
          logo={logo}
          title={title}
          trackEvent={trackEvent}
        />
      );
    }

    if (entryType === 'opensourceProject') {
      const {
        theme,
        description,
        logo,
        services,
        skills,
        product,
        repoData,
        facts,
        productsPage,
        externalLink,
        linkMain,
        hideTags,
      } = entryNode;

      return (
        <OpensourceProjectCard
          size={size}
          slug={slug}
          title={title}
          theme={theme}
          description={description}
          logo={logo}
          services={services}
          skills={skills}
          product={product}
          repoData={repoData}
          facts={facts}
          withEntityType={withEntityType}
          productsPage={productsPage}
          externalLink={externalLink}
          linkMain={linkMain}
          trackEvent={trackEvent}
          hideTags={hideTags}
        />
      );
    }

    return null;
  };

  const renderTile = (entry: any, index: number) => {
    const mediumTileIndex = (index - largeTilesCount) % 10;
    const entity = entry.node || entry;
    const large = index < largeTilesCount;

    return (
      <li
        className={cx(cs.tile, {
          [cs.largeTile]: large,
          [cs[`mediumTile${mediumTileIndex}`]]: index >= largeTilesCount,
        })}
        key={`${entity.entryType}-${entity.slug || entity.title}-${large ? 'lg' : 'md'}-${index}`}
      >
        {renderCard(entry, index)}
      </li>
    );
  };

  const withLargeTiles = largeTilesCount > 0;
  const list = [...entries];
  const subscribeIndex = withLargeTiles ? 7 : 3;
  if (withSubscribe && list.length >= 6) {
    list.splice(subscribeIndex - largeTilesCount, 0, {
      entryType: 'subscribe',
    });
  }

  return (
    <ul className={cx(cs.root, { [cs.withLargeTiles]: largeTilesCount > 0 })}>
      {list.map(renderTile)}
    </ul>
  );
};
